import React, { useEffect, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Link } from '@astryxdesign/core/Link'
import { Table, proportional, pixel } from '@astryxdesign/core/Table'
import type { TableColumn } from '@astryxdesign/core/Table'
import { Text } from '@astryxdesign/core/Text'
import { TextInput } from '@astryxdesign/core/TextInput'
import { Token } from '@astryxdesign/core/Token'
import LandingHeader from './LandingHeader'
import SettingsPanel, { type SettingsSection } from './SettingsPanel'
import { authClient } from '../lib/auth-client'
import type { AppSettings } from '../App'

interface StoryTableRow extends Record<string, unknown> {
  id: string
  name: string
  genre: string
  summary: string
  createdAt: number
  updatedAt: number
}

const TOKEN_COLORS = ['blue', 'purple', 'teal', 'orange', 'pink'] as const

function genreColor(genre: string): (typeof TOKEN_COLORS)[number] {
  const hash = [...genre].reduce((total, character) => total + character.charCodeAt(0), 0)
  return TOKEN_COLORS[hash % TOKEN_COLORS.length]
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, { dateStyle: 'medium' })
}

export default function AllStoriesView(): React.JSX.Element {
  const { data: session } = authClient.useSession()
  const [stories, setStories] = useState<StoryMetadata[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [authorProfile, setAuthorProfile] = useState<AuthorProfile | null>(null)
  const [isProfileLoaded, setIsProfileLoaded] = useState(false)
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('account')
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    let active = true
    window.api.stories
      .list()
      .then((items) => {
        if (active) setStories([...items].sort((a, b) => b.updatedAt - a.updatedAt))
      })
      .catch((reason) => {
        if (active) setError(reason instanceof Error ? reason.message : 'Unable to load stories.')
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!session?.user) return
    let active = true
    Promise.all([window.api.profiles.getActive(), window.api.settings.get()])
      .then(([profile, loadedSettings]) => {
        if (!active) return
        setAuthorProfile(profile)
        setSettings(loadedSettings)
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsProfileLoaded(true)
      })
    return () => {
      active = false
    }
  }, [session?.user])

  const openSettings = (section: 'account' | 'profile'): void => {
    setSettingsSection(section)
    void window.api.settings
      .get()
      .then((latestSettings) => {
        setSettings(latestSettings)
        setShowSettings(true)
      })
      .catch(() => setShowSettings(true))
  }

  const saveSettings = (updatedSettings: AppSettings): void => {
    setSettings(updatedSettings)
    localStorage.setItem('ninenights-settings', JSON.stringify(updatedSettings))
    void window.api.settings.save(updatedSettings)
  }

  const syncTheme = (theme: AppSettings['theme']): void => {
    setSettings((current) => (current ? { ...current, theme } : current))
  }

  const saveProfile = async (changes: {
    penName: string
    authorBlurb: string
    libraryPublic: boolean
  }): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.update(authorProfile.id, changes)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const uploadAvatar = async (file: File): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.uploadAvatar(authorProfile.id, file)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const removeAvatar = async (): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.removeAvatar(authorProfile.id)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const selectDefaultAvatar = async (defaultAvatarId: string): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.selectDefaultAvatar(
      authorProfile.id,
      defaultAvatarId
    )
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const accountDisplayName =
    authorProfile?.penName.trim() || session?.user?.name?.trim() || undefined
  const isAccountLoading = Boolean(session?.user) && !isProfileLoaded

  const rows = useMemo<StoryTableRow[]>(() => {
    const query = searchQuery.trim().toLocaleLowerCase()
    return stories
      .filter((story) => {
        if (!query) return true
        return [story.name, story.genre, story.summary].some((value) =>
          value?.toLocaleLowerCase().includes(query)
        )
      })
      .map((story) => ({
        id: story.id,
        name: story.name,
        genre: story.genre?.trim() ?? '',
        summary: story.summary?.trim() ?? '',
        createdAt: story.createdAt,
        updatedAt: story.updatedAt
      }))
  }, [searchQuery, stories])

  const columns = useMemo<TableColumn<StoryTableRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Story',
        width: proportional(2),
        renderCell: (story) => (
          <VStack gap={1}>
            <Link href={`/drafts/${encodeURIComponent(story.id)}/canvas`} isStandalone>
              {story.name}
            </Link>
            {story.summary && <Text type="supporting">{story.summary}</Text>}
          </VStack>
        )
      },
      {
        key: 'genre',
        header: 'Genre',
        width: proportional(1),
        renderCell: (story) => {
          const genres = story.genre
            .split(',')
            .map((genre) => genre.trim())
            .filter(Boolean)
          return genres.length > 0 ? (
            <HStack gap={1} wrap="wrap">
              {genres.map((genre) => (
                <Token key={genre} label={genre} color={genreColor(genre)} size="sm" />
              ))}
            </HStack>
          ) : (
            <Text type="supporting">Not set</Text>
          )
        }
      },
      {
        key: 'createdAt',
        header: 'Created',
        width: pixel(150),
        renderCell: (story) => formatDate(story.createdAt)
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        width: pixel(150),
        renderCell: (story) => formatDate(story.updatedAt)
      }
    ],
    []
  )

  return (
    <main className="published-story-shell all-stories-page-shell">
      <LandingHeader
        showNavLinks={false}
        showAppButton={false}
        isAppHeader
        isAccountLoading={isAccountLoading}
        userName={accountDisplayName}
        avatarUrl={authorProfile?.avatarUrl}
        onOpenSettings={openSettings}
        currentTheme={settings?.theme}
        onThemeChange={syncTheme}
      />
      <section className="all-stories-page">
        <VStack gap={6}>
          <HStack gap={4} justify="between" align="end" wrap="wrap">
            <VStack gap={1}>
              <h1>My Stories</h1>
              <Text type="supporting">Browse your stories, ordered by most recently updated.</Text>
            </VStack>
            <section className="all-stories-search">
              <TextInput
                label="Search stories"
                isLabelHidden
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search stories"
                startIcon={<Search size={16} />}
                hasClear
              />
            </section>
          </HStack>

          {isLoading ? (
            <Text type="supporting">Loading stories…</Text>
          ) : error ? (
            <Text type="supporting">{error}</Text>
          ) : stories.length === 0 ? (
            <Text type="supporting">You have not created any stories yet.</Text>
          ) : rows.length === 0 ? (
            <Text type="supporting">No stories match “{searchQuery.trim()}”.</Text>
          ) : (
            <Table
              data={rows}
              columns={columns}
              idKey="id"
              density="balanced"
              dividers="rows"
              hasHover
              verticalAlign="top"
            />
          )}
        </VStack>
      </section>
      {showSettings && settings && (
        <SettingsPanel
          settings={settings}
          profile={authorProfile}
          initialSection={settingsSection}
          onSaveSettings={saveSettings}
          onSaveProfile={saveProfile}
          onUploadAvatar={uploadAvatar}
          onRemoveAvatar={removeAvatar}
          onSelectDefaultAvatar={selectDefaultAvatar}
          onClose={() => setShowSettings(false)}
        />
      )}
    </main>
  )
}
